import { Suspense, useRef } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button, Toaster, ErrorBoundary, AppHeader, useMsalAccount } from '@higgins/ui';
import { CrmSwitchGate } from '../components/crm-switch-gate';
import { OutdoorToggle } from '../components/outdoor-toggle';
import { SunTips } from '../components/sun-tips';
import { PageLoading } from '../components/page-loading';
import { sendFeedback } from '../lib/api';
import { countWaiting, getJob } from '../lib/device-db';
import { useDeviceQuery, useHeaderHeight } from '../lib/hooks';
import { useOutdoorPage } from '../lib/outdoor';
import { screenName } from '../lib/screen-name';
import { msalInstance, signOut } from '../lib/msal';
import helpContent from '../../docs/USERS.md?raw';

export default function Layout() {
  // MSAL guarantees an account before the app renders (main.tsx forces login).
  const account = useMsalAccount(msalInstance);
  // The desk's Take Off is the template's wide sheet, so it uses the window up to 1920px (#441); every other screen keeps the iPad width.
  const { pathname } = useLocation();
  const width = pathname.includes('/take-off') ? 'max-w-[120rem]' : 'max-w-4xl';
  // A report raised inside a job carries its number, read from the device (works offline).
  const { jobId } = useParams();
  const jobNumber = useDeviceQuery(async () => (jobId ? (await getJob(jobId))?.number : undefined), [jobId]);
  // The job's sync bar sticks under the header, not over it (#652).
  const shell = useRef<HTMLDivElement>(null);
  useHeaderHeight(shell);
  useOutdoorPage();

  return (
    <div ref={shell} className="min-h-dvh bg-gradient-to-br from-slate-50 via-slate-100 to-orange-50 outdoor:bg-white outdoor:bg-none">
      <AppHeader
        title="Measure2Quote"
        subtitle="Measure on site, build the scope"
        // Outdoor: a solid white header, not see-through over the page.
        className="outdoor:bg-white outdoor:backdrop-blur-none"
        containerClassName={width}
        homeLink={(p) => <Link to="/" {...p} />}
        feedback={{
          send: sendFeedback,
          accountId: account?.localAccountId,
          appKey: 'measure-to-quote',
          jobRef: jobNumber,
          changesToSync: countWaiting,
          screenName: screenName(pathname),
        }}
        help={{ source: helpContent, title: 'Measure2Quote — Help' }}
      >
        {account && (
          <div className="hidden items-center gap-1 sm:flex">
            <span className="max-w-[140px] truncate text-xs text-slate-500" title={account.username}>
              {account.name ?? account.username}
            </span>
          </div>
        )}
        <OutdoorToggle />
        <SunTips />
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="text-slate-500 hover:text-slate-900"
          title={`Sign out ${account?.username ?? ''}`.trim()}
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </AppHeader>

      <main className={`mx-auto ${width} px-4 sm:px-6 pb-16 pt-6`}>
        <ErrorBoundary>
          {/* Pages read async jotai atoms that suspend on first render. */}
          <Suspense fallback={<PageLoading />}>
            <CrmSwitchGate>
              <Outlet />
            </CrmSwitchGate>
          </Suspense>
        </ErrorBoundary>
      </main>

      {import.meta.env.VITE_APP_VERSION && (
        <footer className="pb-3 text-center text-[10px] text-slate-400">
          {import.meta.env.VITE_APP_VERSION}
        </footer>
      )}

      <Toaster position="top-center" />
    </div>
  );
}
